import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone, MessageCircle, MessageSquare, ShieldCheck, Car, Bike, HeartPulse, Droplet, Loader2, PhoneCall, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScanPage() {
  const { tagId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({ telephony: 'mocked' });
  const [modal, setModal] = useState(null); // { method, step, phone, message }

  useEffect(() => {
    (async () => {
      try {
        const [v, c] = await Promise.all([
          axios.get(`${API}/scan/${tagId}`),
          axios.get(`${API}/config`).catch(() => ({ data: { telephony: 'mocked' } })),
        ]);
        setVehicle(v.data);
        setConfig(c.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Tag not found. It may not be activated yet.' : 'Something went wrong. Please try again.');
      } finally { setLoading(false); }
    })();
  }, [tagId]);

  const openMethod = (method) => {
    setModal({ method, step: 'input', phone: '', message: '' });
  };

  const initiate = async () => {
    if (config.telephony === 'exotel') {
      if (!/^\+91[6-9]\d{9}$/.test(modal.phone.replace(/\s/g, ''))) {
        setModal((m) => ({ ...m, error: 'Please enter a valid Indian phone number in E.164 format (e.g. +919999999999)' }));
        return;
      }
    }
    setModal((m) => ({ ...m, step: 'dialing', error: null }));
    try {
      const payload = { method: modal.method };
      if (modal.phone) payload.visitor_phone = modal.phone;
      if (modal.message) payload.message = modal.message;
      const { data } = await axios.post(`${API}/scan/${tagId}/contact`, payload);
      setTimeout(() => setModal((m) => ({ ...m, step: 'connected', response: data })), config.telephony === 'exotel' ? 800 : 2200);
    } catch (e) {
      setModal((m) => ({ ...m, step: 'input', error: e.response?.data?.detail || 'Failed to connect' }));
    }
  };

  const close = () => setModal(null);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-navy-deep text-white">
      <Loader2 size={40} className="animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-navy-deep text-white p-6">
      <div className="text-center max-w-sm">
        <div className="font-extrabold text-4xl tracking-wide">CONNECT</div>
        <div className="font-serif-display italic text-blue-200">near me</div>
        <p className="mt-8 text-lg">{error}</p>
      </div>
    </div>
  );

  const isBike = vehicle?.type === 'bike';
  const VehicleIcon = isBike ? Bike : Car;

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      <div className="max-w-md mx-auto px-6 py-10 flex flex-col min-h-screen">
        <div className="text-center mb-8">
          <div className="inline-flex flex-col leading-none">
            <span className="font-extrabold text-3xl tracking-wide">CONNECT</span>
            <span className="font-serif-display italic text-sm text-blue-200 mt-1">near me</span>
          </div>
          <p className="mt-4 text-blue-200 text-sm">Reach the vehicle owner without seeing their number.</p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 border border-white/15">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center"><VehicleIcon size={22} /></div>
            <div>
              <p className="font-bold">{vehicle.make} {vehicle.model}</p>
              <p className="text-xs text-blue-200">{vehicle.number_plate}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-5">
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-blue-200 text-[10px] uppercase tracking-wider"><Droplet size={11} /> Blood group</div>
              <p className="font-semibold text-sm mt-1">{vehicle.blood_group || 'Not shared'}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-blue-200 text-[10px] uppercase tracking-wider"><HeartPulse size={11} /> Emergency</div>
              <p className="font-semibold text-sm mt-1">{vehicle.emergency_contact ? 'Available' : 'Not shared'}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <p className="text-blue-200 text-xs uppercase tracking-widest text-center mb-3">Contact the owner</p>
          <button onClick={() => openMethod('call')} className="w-full bg-white text-navy rounded-2xl p-4 flex items-center gap-4 hover:bg-blue-50 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center"><Phone size={20} /></div>
            <div className="text-left"><p className="font-semibold">Masked call</p><p className="text-xs text-neutral-500">Talk now — owner&apos;s number stays private</p></div>
          </button>
          <button onClick={() => openMethod('whatsapp')} className="w-full bg-white text-navy rounded-2xl p-4 flex items-center gap-4 hover:bg-blue-50 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-green-500 text-white flex items-center justify-center"><MessageCircle size={20} /></div>
            <div className="text-left"><p className="font-semibold">Private WhatsApp</p><p className="text-xs text-neutral-500">Chat through our privacy relay</p></div>
          </button>
          <button onClick={() => openMethod('sms')} className="w-full bg-white text-navy rounded-2xl p-4 flex items-center gap-4 hover:bg-blue-50 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-blue-500 text-white flex items-center justify-center"><MessageSquare size={20} /></div>
            <div className="text-left"><p className="font-semibold">SMS</p><p className="text-xs text-neutral-500">Leave a masked text message</p></div>
          </button>
        </div>

        <div className="mt-8 bg-white/5 rounded-2xl p-4 flex items-start gap-3">
          <ShieldCheck size={18} className="text-blue-200 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-200 leading-relaxed">No number is ever revealed on either side. Powered by Connect Near Me, a venture of Explore Biz.</p>
        </div>

        <div className="mt-auto pt-8 text-center text-[11px] text-blue-300">Tag ID · {tagId} · {config.telephony === 'exotel' ? 'Live' : 'Demo'} mode</div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white text-navy rounded-3xl p-6 max-w-sm w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-semibold text-lg capitalize">
                {modal.method === 'call' ? 'Masked call' : modal.method === 'whatsapp' ? 'Private WhatsApp' : 'Masked SMS'}
              </h3>
              <button onClick={close} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button>
            </div>

            {modal.step === 'input' && (
              <>
                {config.telephony === 'exotel' ? (
                  <>
                    <p className="text-sm text-neutral-600 mb-4">Enter your phone. Exotel will call you first, then bridge you to the owner. Neither side sees the other&apos;s number.</p>
                    <label className="text-xs font-medium">Your phone (with country code)</label>
                    <input value={modal.phone} onChange={(e) => setModal({ ...modal, phone: e.target.value })} placeholder="+91 99999 99999" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" />
                    {modal.method !== 'call' && (
                      <>
                        <label className="text-xs font-medium mt-3 block">Message (optional)</label>
                        <textarea rows={2} value={modal.message} onChange={(e) => setModal({ ...modal, message: e.target.value })} placeholder="Please move your vehicle." className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none resize-none text-sm" />
                      </>
                    )}
                    {modal.error && <p className="text-xs text-red-600 mt-2">{modal.error}</p>}
                    <button onClick={initiate} className="btn-primary w-full justify-center mt-4">Connect now</button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600 mb-4">Demo mode. In production Exotel would ring you first, then bridge the owner privately using our Exophone.</p>
                    <div className="bg-navy/5 rounded-lg p-3 text-xs text-navy mb-3">
                      Set <code className="font-mono">EXOTEL_SID</code>, <code className="font-mono">EXOTEL_API_KEY</code>, <code className="font-mono">EXOTEL_API_TOKEN</code> and <code className="font-mono">EXOTEL_VIRTUAL_NUMBER</code> in backend <code className="font-mono">.env</code> to enable real telephony.
                    </div>
                    <button onClick={initiate} className="btn-primary w-full justify-center">Try demo flow</button>
                  </>
                )}
              </>
            )}

            {modal.step === 'dialing' && (
              <div className="text-center py-2">
                <div className="relative mx-auto w-20 h-20 mb-4">
                  <div className="absolute inset-0 rounded-full bg-navy/10 animate-ping"></div>
                  <div className="absolute inset-0 rounded-full bg-navy flex items-center justify-center">
                    <PhoneCall size={28} className="text-white" />
                  </div>
                </div>
                <p className="font-semibold">Connecting to owner…</p>
                <p className="text-xs text-neutral-500 mt-1">Routing your {modal.method} through a masked relay.</p>
                {config.telephony !== 'exotel' && <p className="text-[10px] text-neutral-400 mt-4">DEMO · Real telephony requires Exotel keys</p>}
              </div>
            )}

            {modal.step === 'connected' && (
              <div className="text-center py-2">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500 text-white flex items-center justify-center"><ShieldCheck size={32} /></div>
                <p className="font-semibold">
                  {modal.method === 'call' ? 'Call routed' : modal.response?.status === 'sent' ? 'Message sent' : 'Connected securely'}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {config.telephony === 'exotel'
                    ? (modal.method === 'call' ? 'Answer the incoming call from our Exophone to speak to the owner.' : 'The owner will get your message shortly.')
                    : `The owner would be notified of your ${modal.method}.`}
                </p>
                <button onClick={close} className="btn-primary mt-5 w-full justify-center">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
